import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef, ReactNode } from 'react';
import getSocket from 'services/socket';
import { useCurrentUser } from 'hooks/selectors';
import { Vehicle } from 'types/vehicle';

interface Editor {
  id: number;
  first_name: string;
  last_name: string;
}

interface EditorsListEvent {
  vehicleId: number;
  editors: Editor[];
  firstEditorId: number | null;
}

interface UserJoinedEvent {
  vehicleId: number;
  user: Editor;
  firstEditorId: number;
}

interface UserLeftEvent {
  vehicleId: number;
  user: { id: number; first_name?: string; last_name?: string };
  firstEditorId: number | null;
}

interface VehicleEditingContextValue {
  hasAnotherEditor: boolean;
  warningText: string | null;
}

const VehicleEditingContext = createContext<VehicleEditingContextValue | null>(null);

export const useVehicleEditing = () => {
  const ctx = useContext(VehicleEditingContext);
  if (!ctx) {
    return { hasAnotherEditor: false, warningText: '' };
  }
  return ctx;
};

interface Props {
  vehicle: Vehicle;
  children: ReactNode;
}

const HEARTBEAT_INTERVAL = 20000;

export const VehicleEditingProvider = ({ vehicle, children }: Props) => {
  const socket = getSocket();
  const user = useCurrentUser();
  const [editors, setEditors] = useState<Editor[]>([]);
  const [firstEditorId, setFirstEditorId] = useState<number | null>(null);
  const heartbeatRef = useRef<number | null>(null);

  const startHeartbeat = useCallback((vehicleId: number, userId: number) => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    heartbeatRef.current = setInterval(() => {
      socket.emit('heartbeat-edit-vehicle', `${vehicleId}:${userId}`);
    }, HEARTBEAT_INTERVAL);
  }, [socket]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!vehicle || vehicle.status === 'draft') return;

    const channel = `${vehicle.id}:${user.id}`;

    const handleEditorsList = (data: EditorsListEvent) => {
      if (data.vehicleId === vehicle.id) {
        setEditors(data.editors);
        setFirstEditorId(data.firstEditorId);
      }
    };

    const handleUserJoined = (data: UserJoinedEvent) => {
      if (data.vehicleId === vehicle.id) {
        setEditors(prev => {
          if (prev.some(e => e.id === data.user.id)) return prev;
          return [...prev, data.user];
        });
        setFirstEditorId(data.firstEditorId);
      }
    };

    const handleUserLeft = (data: UserLeftEvent) => {
      if (data.vehicleId === vehicle.id) {
        setEditors(prev => prev.filter(e => e.id !== data.user.id));
        setFirstEditorId(data.firstEditorId);
      }
    };

    const handleBeforeUnload = () => {
      socket.emit('unsubscribe-edit-vehicle', channel);
    };

    socket.emit('subscribe-edit-vehicle', channel);
    socket.on('edit-vehicle:editors-list', handleEditorsList);
    socket.on('edit-vehicle:user-joined', handleUserJoined);
    socket.on('edit-vehicle:user-left', handleUserLeft);

    startHeartbeat(vehicle.id, user.id);

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      socket.off('edit-vehicle:editors-list', handleEditorsList);
      socket.off('edit-vehicle:user-joined', handleUserJoined);
      socket.off('edit-vehicle:user-left', handleUserLeft);
      socket.emit('unsubscribe-edit-vehicle', channel);
      stopHeartbeat();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [vehicle.id, vehicle.status, user.id, socket, startHeartbeat, stopHeartbeat]);

  const otherEditors = useMemo(() => {
    return editors.filter(e => e.id !== user.id);
  }, [editors, user.id]);

  const isFirstEditor = firstEditorId !== null && user.id === firstEditorId;
  const showWarning = editors.length > 1 && !isFirstEditor;

  const warningText = useMemo(() => {
    if (!showWarning) return null;

    if (otherEditors.length === 1) {
      const editor = otherEditors[0];
      const name = `${editor.first_name} ${editor.last_name}`.trim() || 'Another user';
      return `${name} is already editing this vehicle. Please contact them if you want to make changes`;
    }

    const names = otherEditors.map(e => `${e.first_name} ${e.last_name}`.trim()).filter(Boolean);
    return `${names.join(', ')} are also editing this vehicle. Please coordinate to avoid conflicts`;
  }, [showWarning, otherEditors]);

  return (
    <VehicleEditingContext.Provider
      value={{
        hasAnotherEditor: showWarning,
        warningText
      }}
    >
      {children}
    </VehicleEditingContext.Provider>
  );
};
